import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/TextArea";
import { Sparkles, Loader2, RefreshCw, Mic, MicOff } from "lucide-react";
import { useSpeechToText } from "@/hooks/useSpeechToText";
import { useState, useEffect } from "react";
import { useGetWorkflowSuggestionsMutation } from "@/services/WorkFlowBuilderApi";
import { useGenerateAIMutation } from "@/services/FormBuilderApi";
import { WorkflowCreate, WorkflowSuggestion } from "@/app/types/workflow";
import { useToast } from "@/components/ui/UseToast";

interface AIWorkflowModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onWorkflowGenerated: (workflow: WorkflowCreate) => void;
}

export const AIWorkflowModal = ({
  isOpen,
  onOpenChange,
  onWorkflowGenerated,
}: AIWorkflowModalProps) => {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState<WorkflowSuggestion[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isViewMoreOpen, setIsViewMoreOpen] = useState(false);
  const [getSuggestions] = useGetWorkflowSuggestionsMutation();
  const [generateAI] = useGenerateAIMutation();
  const { toast } = useToast();


  const handleTranscript = (text: string) => {
    setPrompt((prev) => prev + (prev ? " " : "") + text);
  };

  const { isListening, toggleListening, hasSupport } = useSpeechToText({
    onTranscript: handleTranscript,
  });

  useEffect(() => {
    if (isOpen && suggestions.length === 0) {
      fetchSuggestions();
    }
  }, [isOpen]);

  const fetchSuggestions = async () => {
    setIsLoadingSuggestions(true);
    try {
      const response = await getSuggestions().unwrap();
      const items = response.data["Workflow Suggestions"] || [];

      const mappedSuggestions: WorkflowSuggestion[] = items.map((item) => ({
        title: `Suggestion ${item.id}`,
        prompt: item.value.suggestion,
      }));

      setSuggestions(mappedSuggestions);
    } catch (error) {
      console.error("Failed to fetch suggestions", error);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a workflow description.",
      });
      return;
    }

    setIsGenerating(true);

    try {
      const aiResponse = await generateAI({ type: "workflow", user_prompt: prompt, name: "Workflow" }).unwrap();
      toast({
        variant: "success",
        title: "Success",
        description: "Workflow generated successfully!",
      });

      onWorkflowGenerated(aiResponse);
      setPrompt("");
      onOpenChange(false);
    } catch (error: any) {
      console.error("Workflow generation error:", error);
      toast({
        variant: "destructive",
        title: "Generation Failed",
        description: error.message || "Failed to generate workflow. Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-600" />
            AI Workflow Assistant
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
            Describe the workflow you want to build, and I'll help you create it.
            You can ask me to create triggers, actions, or complex logic flows.
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Try these ideas:
                </span>
                {/* <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={fetchSuggestions}
                  disabled={isLoadingSuggestions}
                >
                  <RefreshCw className={`h-3 w-3 mr-1 ${isLoadingSuggestions ? "animate-spin" : ""}`} />
                  Refresh
                </Button> */}
              </div>

              {isLoadingSuggestions ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-16 rounded-md border bg-muted animate-pulse" />
                  ))}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {suggestions.slice(0, 4).map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => setPrompt(suggestion.prompt)}
                        className="text-left p-3 rounded-md border bg-background hover:bg-accent hover:text-accent-foreground transition-colors text-xs"
                      >
                        <div className="font-medium mb-1">{suggestion.title}</div>
                        <div className="text-muted-foreground line-clamp-2">
                          {suggestion.prompt}
                        </div>
                      </button>
                    ))}
                  </div>
                  {suggestions.length > 4 && (
                    <div className="mt-2 flex justify-center">
                      <Button
                        variant="link"
                        size="sm"
                        className="text-xs text-muted-foreground hover:text-primary"
                        onClick={() => setIsViewMoreOpen(true)}
                      >
                        View More Suggestions
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <Dialog open={isViewMoreOpen} onOpenChange={setIsViewMoreOpen}>
            <DialogContent className="sm:max-w-[700px] max-h-[80vh] flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-yellow-600" />
                  All Workflow Suggestions
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto pr-2 py-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setPrompt(suggestion.prompt);
                        setIsViewMoreOpen(false);
                      }}
                      className="text-left p-4 rounded-lg border bg-background hover:bg-accent hover:text-accent-foreground transition-all duration-200 group text-xs shadow-sm hover:shadow-md"
                    >
                      <div className="font-semibold mb-2 group-hover:text-primary transition-colors flex items-center justify-between">
                        {suggestion.title}
                        <Sparkles className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="text-muted-foreground leading-relaxed">
                        {suggestion.prompt}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <div className="relative">
            <Textarea
              placeholder="e.g., Create a workflow that sends an email when a new form is submitted..."
              className="min-h-[120px] pr-12"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isGenerating}
            />
            {hasSupport && (
              <Button
                variant="ghost"
                size="icon"
                className={`absolute right-2 top-2 h-8 w-8 transition-colors ${isListening
                  ? "text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100"
                  : "text-muted-foreground hover:text-foreground"
                  }`}
                onClick={toggleListening}
                title={isListening ? "Stop listening" : "Start voice input"}
                type="button"
              >
                {isListening ? (
                  <MicOff className="h-4 w-4 animate-pulse" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setPrompt("");
                onOpenChange(false);
              }}
              className="w-30"
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              className="min-w-[100px]"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

